import { Fragment, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import getWebsiteUrl from '@cc/shared/utils/getWebsiteUrl';
import getRandomId from '@cc/shared/utils/getRandomId';
import { useCalculatorId, useIsTesting } from '../CalculatorState';
import { borderRadius, colors } from '../styles';
import fetchJson from '../util/fetchJson';
import getRootWindow from '../util/getRootWindow';
import Icon from './Icon';
import generate from '../util/generatePdf';
import { useFeatureFlags } from '../CalculatorState/CalculatorState';

const OutlineWrapper = styled.div`
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  @media print {
    display: none;
  }
`;

const Flex = styled.div<{ gap?: string }>`
  display: flex;
  gap: ${(props) => props.gap || '0.5rem'};
  align-items: center;
`;

const PrintButton = styled.div`
  padding: 0.25rem 0.75rem;
  background: ${colors['gray-200']};
  color: ${colors['gray-800']};
  border: 1px solid ${colors['gray-300']};
  border-radius: ${borderRadius.lg};
  line-height: 2.25;
  font-size: 0.75rem;
  font-weight: 600;
  &:hover {
    cursor: pointer;
    background: ${colors['gray-300']};
  }
`;

const BadgeContainer = styled.div<{ isHovered: boolean; brandingType: string }>`
  border-radius: 10px;
  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.06),
    0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  width: 180px;
  overflow: visible;
  position: relative;
  z-index: 1000;
  transform: ${(props) =>
    props.isHovered
      ? props.brandingType === 'Template'
        ? 'translate3d(0, -115px, 0)'
        : 'translate3d(0, -100px, 0)'
      : 'translate3d(0, 0, 0)'};
  transition: transform 0.4s cubic-bezier(0.175, 0.84, 0.44, 1.05);
  will-change: transform;
  margin-left: 5px;
  bottom: 5px;
`;

const BadgeTop = styled.div<{ isHovered: boolean }>`
  border-radius: ${(props) => (props.isHovered ? '10px 10px 0 0' : '10px')};
  border-bottom: 0.5px solid var(--Neutral-200, #e7e5e4);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.94) 0%,
    rgba(255, 255, 255, 0.88) 100%
  );
  box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.64);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  padding: 0.375rem 0.5rem;
`;

const BadgeLogo = styled.img`
  height: 22px;
  width: 22px;
  border-radius: 4px;
  margin-right: 0.5rem;
`;

const BadgeTitleText = styled.span`
  font-family: Inter, system-ui, sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  color: #000;
  cursor: default;
`;

const BadgeBottom = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  border-radius: 0 0 10px 10px;
  background: #fff;
  padding: 0.5rem;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.1),
    0px 4px 16px 0px rgba(0, 0, 0, 0.12);
`;

const BadgeDescription = styled.div`
  font-family: Inter, system-ui, sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 14px;
  color: #000;
  margin-bottom: 0.5rem;
`;

const BadgeButton = styled.button`
  width: 100%;
  padding: 0.375rem 0.5rem;
  border: none;
  border-radius: var(--radius-default, 8px);
  background: var(
    --Brand-1-Gradient-light,
    linear-gradient(180deg, #2741e7 0%, #1027bc 100%)
  );
  box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.24),
    0px 1px 1px 0px rgba(0, 0, 0, 0.04), 0px 2px 8px 0px rgba(0, 0, 0, 0.06);
  color: white;
  font-family: Inter, system-ui, sans-serif;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.25rem;
  &:hover {
    background: #1c32c8;
  }
`;

const getLink = ({ title, brandingType, calculatorId }) => {
  const rootWindow = getRootWindow();
  if (!rootWindow) return 'http://www.convertcalculator.com';
  const { hostname, href } = rootWindow.location;
  if (brandingType === 'Template') {
    return `https://www.convertcalculator.com/app/create/?calculatorId=${calculatorId}&utm_source=${hostname}&utm_medium=template&utm_campaign=capture`;
  }
  return `http://www.convertcalculator.com/?utm_source=${hostname}&utm_medium=embed&utm_campaign=${title}&utm_content=${href}`;
};

const Badge = ({ brandingType }) => {
  const [isHovered, setIsHovered] = useState(false);
  const calculatorId = useCalculatorId();
  const link = getLink({ title: 'Built with Convert_', brandingType, calculatorId });

  let description = '';
  let buttonLabel = '';

  if (brandingType === 'Template') {
    description =
      'Duplicate this template, add your questions, pricing, and branding, and embed it to your own website.';
    buttonLabel = 'Duplicate Template';
  } else {
    description =
      'Create powerful calculators, quote forms & lead funnels. No tech skills required.';
    buttonLabel = 'Learn More';
  }

  return (
    <BadgeContainer
      isHovered={isHovered}
      brandingType={brandingType}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BadgeTop isHovered={isHovered}>
        <BadgeLogo
          src="https://www.convertcalculator.com/img/favicon.png"
          alt="ConvertCalculator logo"
        />
        <BadgeTitleText>Built with Convert_</BadgeTitleText>
      </BadgeTop>
      {isHovered && (
        <BadgeBottom>
          <BadgeDescription>{description}</BadgeDescription>
          <BadgeButton
            onClick={(e) => {
              e.stopPropagation();
              window.open(link, '_blank');
            }}
          >
            {buttonLabel}
          </BadgeButton>
        </BadgeBottom>
      )}
    </BadgeContainer>
  );
};

const BadgeWrapper = (props) => {
  const badgeObserverRef = useRef(null);
  const badgeKeyRef = useRef(1);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      badgeKeyRef.current += 1;
    });

    observer.observe(badgeObserverRef.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Fragment key={badgeKeyRef.current}>
      <div ref={badgeObserverRef}>
        <Badge {...props} />
      </div>
    </Fragment>
  );
};

const useShowBranding = () => {
  const [brandingInfo, setBrandingInfo] = useState({
    plan: null,
    forceBranding: null,
    brandingType: null,
  });
  const isTesting = useIsTesting();
  const calculatorId = useCalculatorId();

  useEffect(() => {
    if (isTesting) return;
    const getShowBranding = async () => {
      try {
        const url = new URL(
          getWebsiteUrl({
            slug: '/api/embed/branding-v2',
          }),
        );
        url.searchParams.append('calculatorId', calculatorId);

        const { plan, forceBranding, brandingType } = await fetchJson({
          url: url.toString(),
          method: 'GET',
        });
        setBrandingInfo({ plan, forceBranding, brandingType });
      } catch (error) {
        console.error(`Could not fetch branding: ${error}`);
      }
    };

    getShowBranding();
  }, [isTesting, calculatorId]);

  return brandingInfo;
};

const Outline = ({ calculator, embedType }) => {
  const brandingInfo = useShowBranding();

  const shouldShowBranding = () => {
    if (!brandingInfo) return false;
    const { plan, forceBranding, brandingType } = brandingInfo;

    if (brandingType === 'None') return false;
    if (brandingType === 'Template') return true;
    if (brandingType === 'Branded') return true;
    return plan === 'free' || forceBranding;
  };

  const displayBranding = shouldShowBranding();

  useEffect(() => {
    console.log(
      '💡 The interactive content on this page is powered by https://www.convertcalculator.com',
    );
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const flags = useFeatureFlags();
  const generatePdfFlag = flags.generatePdf ?? false;

  const printCalculator = async () => {
    const root = document.querySelector('.cc-calculator');

    if (generatePdfFlag && root != null) {
      setIsLoading(true);

      const filename = `${getRandomId()}.pdf`;
      const blob = await generate(root);
      setIsLoading(false);

      if (blob != null) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        alert('Failed to generate PDF');
      }
    } else {
      window?.print();
    }
  };

  if (embedType === 'standalone') {
    return (
      <OutlineWrapper isStandalone={true}>
        <Flex>
          {calculator.showPrintButton && (
            <PrintButton
              className="print-button"
              role="button"
              onClick={printCalculator}
            >
              <Flex gap="0.5rem">
                <Icon name="print" size={20} />
                <div>{isLoading ? 'Loading...' : 'Print'}</div>
              </Flex>
            </PrintButton>
          )}
          {displayBranding && brandingInfo && (
            <BadgeWrapper
              brandingType={brandingInfo?.brandingType}
              isStandalone={true}
            />
          )}
        </Flex>
      </OutlineWrapper>
    );
  }

  if (displayBranding && brandingInfo) {
    return (
      <BadgeWrapper brandingType={brandingInfo?.brandingType} isStandalone={false} />
    );
  }

  return null;
};

export default Outline;
